import { Box, ButtonBase, Stack, Typography } from "@mui/material";
import { DragEvent, memo, useState } from "react";
import { TpmsSensor, TpmsVehicleType } from "src/models";
import {
  barToPsi,
  celsiusToFahrenheit,
  sensorSeverity,
  slotRowsForVehicleType,
  slotsForVehicleType,
  TpmsSeverity,
  WheelSlotDef,
} from "src/utils/tpms-utils";

export function severityColor(severity: TpmsSeverity): string {
  switch (severity) {
    case 'alert':
      return 'error.main';
    case 'warn':
      return 'warning.main';
    case 'stale':
      return 'grey.500';
    default:
      return 'success.main';
  }
}

type WheelProps = {
  slot: WheelSlotDef,
  sensor?: TpmsSensor,
  selected: boolean,
  isDragging: boolean,
  isDragSource: boolean,
  onTap: (slotIndex: number) => void,
  onDragStart: (sensorId: string) => void,
  onDragEnd: () => void,
  onDrop: (slotIndex: number) => void,
};

const Wheel = ({ slot, sensor, selected, isDragging, isDragSource, onTap, onDragStart, onDragEnd, onDrop }: WheelProps) => {
  const [dragOver, setDragOver] = useState(false);
  const dimmed = isDragging && !isDragSource && !!sensor;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.25,
      }}
    >
      <ButtonBase
        draggable={!!sensor}
        onClick={() => onTap(slot.index)}
        onDragStart={(event: DragEvent) => {
          if (!sensor) return;
          event.dataTransfer.effectAllowed = "move";
          // defer so the drag image is captured before styles change
          const { sensorId } = sensor;
          requestAnimationFrame(() => onDragStart(sensorId));
        }}
        onDragEnd={() => {
          setDragOver(false);
          onDragEnd();
        }}
        onDragOver={(event: DragEvent) => {
          if (!isDragging || isDragSource) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = "move";
        }}
        onDragEnter={() => {
          if (isDragging && !isDragSource) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(event: DragEvent) => {
          if (!isDragging || isDragSource) return;
          event.preventDefault();
          setDragOver(false);
          onDrop(slot.index);
        }}
        sx={{
          width: 58,
          height: 68,
          borderRadius: 1.5,
          // own stacking context so the drag ghost is just the wheel, not the card
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          transition: "filter 0.15s, opacity 0.15s",
          ...(sensor
            ? {
              bgcolor: severityColor(sensorSeverity(sensor)),
              color: "common.white",
              cursor: "grab",
            }
            : {
              border: "2px dashed",
              borderColor: isDragging ? "primary.main" : "divider",
              color: "text.disabled",
            }),
          ...(selected && {
            outline: "3px solid",
            outlineColor: "primary.main",
            outlineOffset: "2px",
          }),
          ...(dimmed && {
            filter: "grayscale(1)",
            opacity: 0.45,
          }),
          ...(isDragSource && {
            opacity: 0.85,
            outline: "3px solid",
            outlineColor: "primary.main",
            outlineOffset: "2px",
          }),
          ...(dragOver && {
            outline: "3px solid",
            outlineColor: "primary.main",
            outlineOffset: "2px",
            filter: "none",
            opacity: 1,
          }),
          ...(isDragging && { "& *": { pointerEvents: "none" } }),
        }}
      >
        {sensor
          ? <>
            <Typography variant="subtitle2" lineHeight={1.2}>
              {barToPsi(sensor.pressureBar).toFixed(1)}
            </Typography>
            <Typography variant="caption" lineHeight={1.2} sx={{ opacity: 0.8 }}>
              psi
            </Typography>
            <Typography variant="caption" lineHeight={1.2}>
              {Math.round(celsiusToFahrenheit(sensor.temperatureC))}°F
            </Typography>
          </>
          : <Typography variant="caption">Empty</Typography>}
      </ButtonBase>
      <Typography variant="caption" color="text.secondary">
        {slot.label}
      </Typography>
    </Box>
  );
};

type VehicleDiagramProps = {
  vehicleType: TpmsVehicleType,
  sensors: TpmsSensor[],
  selectedSensorId: string | null,
  draggingSensorId: string | null,
  onWheelTap: (slotIndex: number) => void,
  onDragStart: (sensorId: string) => void,
  onDragEnd: () => void,
  onDrop: (slotIndex: number) => void,
};

export const VehicleDiagram = memo(({
  vehicleType,
  sensors,
  selectedSensorId,
  draggingSensorId,
  onWheelTap,
  onDragStart,
  onDragEnd,
  onDrop,
}: VehicleDiagramProps) => {
  const slots = slotsForVehicleType(vehicleType);
  const rows = slotRowsForVehicleType(vehicleType);
  const sensorsBySlot = new Map<number, TpmsSensor>();
  sensors.forEach(sensor => {
    if (sensor.wheelSlot !== null && sensor.wheelSlot >= 0 && sensor.wheelSlot < slots.length) {
      sensorsBySlot.set(sensor.wheelSlot, sensor);
    }
  });
  const isDragging = draggingSensorId !== null;

  const renderWheel = (slotIndex: number) => {
    const slot = slots[slotIndex];
    const sensor = sensorsBySlot.get(slotIndex);
    return (
      <Wheel
        key={slot.index}
        slot={slot}
        sensor={sensor}
        selected={!!sensor && sensor.sensorId === selectedSensorId}
        isDragging={isDragging}
        isDragSource={!!sensor && sensor.sensorId === draggingSensorId}
        onTap={onWheelTap}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDrop={onDrop}
      />
    );
  };

  return (
    <Stack spacing={3} sx={{ maxWidth: 420, mx: "auto" }}>
      {rows.map((row, rowIndex) => {
        const leftHalf = row.slice(0, row.length / 2);
        const rightHalf = row.slice(row.length / 2);
        return (
          <Box
            key={rowIndex}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              px: row.length === 2 ? 5 : 0,
            }}
          >
            <Stack direction="row" spacing={1.5}>
              {leftHalf.map(renderWheel)}
            </Stack>
            <Stack direction="row" spacing={1.5}>
              {rightHalf.map(renderWheel)}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
});
